import PropTypes from 'prop-types';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Checkbox,
  Grid,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useProject } from '../../Layout/header/context';
import { useStyles } from '../styles/moreDetailsStyles';
import { PROJECT_STATUS_LIST } from '../../../../Constants/ProjectConstants';
import { getAllClients } from '../../../../services/clientService';
import { updateProject } from '../../../../services/projectService';

const label = { inputProps: { 'aria-label': 'Checkbox demo' } };
const PROJECT_HEALTH_LIST = ['Completed', 'Not Started', 'On Track', 'On Hold', 'Delayed', 'At Risk'];

const PROJECT_RISK_LIST = ['Low', 'Medium', 'High', 'No Risk'];

const MoreDetails = () => {
  const { projectDetails, setProjectDetails, isEditPermission, userIsClient } = useProject();
  const classes = useStyles();
  const [clientList, setClientList] = useState([]);
  const [disabled, setDisabled] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [enteredTotalHrs, setEnteredTotalHrs] = useState(projectDetails?.totalHrs);
  const [enteredInitialEstimatedHrs, setEnteredInitialEstimatedHrs] = useState(projectDetails?.initialEstimatedHrs);

  const handleClientChange = async (e, value) => {
    if (!isEditPermission || value?.id === projectDetails?.clientId) return;
    try {
      setSelectedClient(value);
      setDisabled('clientId');
      await updateProject(projectDetails?.id, { clientId: value?.id });
      setProjectDetails((prev) => ({ ...prev, clientId: value?.id }));
      setSelectedClient(value);
    } catch (error) {
      const errMsg =
        error?.response?.data?.message || `An error occurred while updating the project. Please try again later.`;
      toast.error(errMsg);
    } finally {
      setDisabled(false);
    }
  };

  const handleStatusChange = async (e) => {
    const status = e.target.value;
    if (!isEditPermission || status === projectDetails?.status) return;
    try {
      setDisabled('status');
      await updateProject(projectDetails?.id, { status });
      setProjectDetails((prev) => ({ ...prev, status }));
    } catch (error) {
      const errMsg =
        error?.response?.data?.message || `An error occurred while updating the project. Please try again later.`;
      toast.error(errMsg);
    } finally {
      setDisabled(false);
    }
  };

  const handleHealthChange = async (e) => {
    const health = e.target.value;
    if (!isEditPermission || health === projectDetails?.health) return;
    try {
      setDisabled('health');
      await updateProject(projectDetails?.id, { health });
      setProjectDetails((prev) => ({ ...prev, health }));
    } catch (error) {
      const errMsg =
        error?.response?.data?.message || `An error occurred while updating the project. Please try again later.`;
      toast.error(errMsg);
    } finally {
      setDisabled(false);
    }
  };

  const handleRiskChange = async (e) => {
    const risk = e.target.value;
    if (!isEditPermission || risk === projectDetails?.risk) return;
    try {
      setDisabled('risk');
      await updateProject(projectDetails?.id, { risk });
      setProjectDetails((prev) => ({ ...prev, risk }));
    } catch (error) {
      const errMsg =
        error?.response?.data?.message || `An error occurred while updating the project. Please try again later.`;
      toast.error(errMsg);
    } finally {
      setDisabled(false);
    }
  };

  const hanldeTotalHrsChange = (e) => {
    const inputValue = e.target.value;
    const isValidInput = /^-?\d*\.?\d*$/.test(inputValue);

    if (isValidInput && inputValue >= 0) {
      setEnteredTotalHrs(inputValue);
    }
  };

  const handleTotalHrsBlur = async () => {
    const totalHrs = parseFloat(enteredTotalHrs);
    if (!isEditPermission || totalHrs === projectDetails?.totalHrs) return;
    try {
      setDisabled('totalHrs');
      await updateProject(projectDetails?.id, { totalHrs });
      setProjectDetails((prev) => ({ ...prev, totalHrs }));
    } catch (error) {
      const errMsg =
        error?.response?.data?.message || `An error occurred while updating the project. Please try again later.`;
      toast.error(errMsg);
    } finally {
      setDisabled(false);
    }
  };

  const handleInitialEstimatedHrsChange = (e) => {
    const inputValue = e.target.value;
    const isValidInput = /^-?\d*\.?\d*$/.test(inputValue);

    if (isValidInput && inputValue >= 0) {
      setEnteredInitialEstimatedHrs(inputValue);
    }
  };

  const handleInitialEstimatedHrsBlur = async () => {
    const initialEstimatedHrs = parseFloat(enteredInitialEstimatedHrs);
    if (!isEditPermission || initialEstimatedHrs === projectDetails?.initialEstimatedHrs) return;
    try {
      setDisabled('initialEstimatedHrs');
      await updateProject(projectDetails?.id, { initialEstimatedHrs });
      setProjectDetails((prev) => ({ ...prev, initialEstimatedHrs }));
    } catch (error) {
      const errMsg =
        error?.response?.data?.message || `An error occurred while updating the project. Please try again later.`;
      toast.error(errMsg);
    } finally {
      setDisabled(false);
    }
  };

  const handleCheckChange = (name) => async (event) => {
    try {
      if (!isEditPermission) return;
      const check = event.target.checked;
      setDisabled(name);
      await updateProject(projectDetails?.id, { [name]: check });
      setProjectDetails((prev) => ({ ...prev, [name]: check }));
    } catch (error) {
      const errMsg =
        error?.response?.data?.message || `An error occurred while updating the project. Please try again later.`;
      toast.error(errMsg);
    } finally {
      setDisabled(false);
    }
  };

  /**
   * handleDateChange:
   * - For 'projectLiveDate' we map to 'ProjectLiveDate' when sending to backend
   *   and also update the local projectDetails with ProjectLiveDate key so UI re-renders.
   * - For other date fields we keep the same key as before.
   */
  const handleDateChange = (name) => async (value) => {
    try {
      if (!isEditPermission) return;
      setDisabled(name);
      const formattedValue = value ? dayjs(value).format('YYYY-MM-DD') : null;

      const uatDate = projectDetails?.projectUATDate ? dayjs(projectDetails.projectUATDate, 'YYYY-MM-DD') : null;

      const liveDate = projectDetails?.ProjectLiveDate ? dayjs(projectDetails.ProjectLiveDate, 'YYYY-MM-DD') : null;
      if (name === 'projectLiveDate' && value && uatDate) {
        if (dayjs(value).isBefore(uatDate)) {
          toast.error('Live Date cannot be before UAT Date.');
          setDisabled(false);
          return;
        }
      }

      if (name === 'projectUATDate' && value && liveDate) {
        if (dayjs(value).isAfter(liveDate)) {
          toast.error('UAT Date cannot be after Live Date.');
          setDisabled(false);
          return;
        }
      }
      // Fetch the current start and end dates for comparison
      const startDate = projectDetails?.startDate ? dayjs(projectDetails.startDate, 'YYYY-MM-DD') : null;
      const endDate = projectDetails?.endDate ? dayjs(projectDetails.endDate, 'YYYY-MM-DD') : null;
      const actualStartDate = projectDetails?.actualStartDate
        ? dayjs(projectDetails.actualStartDate, 'YYYY-MM-DD')
        : null;
      const actualEndDate = projectDetails?.actualEndDate ? dayjs(projectDetails.actualEndDate, 'YYYY-MM-DD') : null;

      // VALIDATIONS
      if (name === 'endDate' && value && startDate && dayjs(value).isBefore(startDate)) {
        toast.error('End Date cannot be before Start Date.');
        setDisabled(false);
        return;
      }
      if (name === 'actualEndDate' && value && actualStartDate && dayjs(value).isBefore(actualStartDate)) {
        toast.error('Actual End Date cannot be before Actual Start Date.');
        setDisabled(false);
        return;
      }
      if (name === 'startDate' && value && endDate && dayjs(value).isAfter(endDate)) {
        toast.error('Start Date cannot be after End Date.');
        setDisabled(false);
        return;
      }
      if (name === 'actualStartDate' && value && actualEndDate && dayjs(value).isAfter(actualEndDate)) {
        toast.error('Actual Start Date cannot be after Actual End Date.');
        setDisabled(false);
        return;
      }

      // Special mapping for backend key: projectLiveDate -> ProjectLiveDate
      if (name === 'projectLiveDate') {
        // send PascalCase key to backend
        await updateProject(projectDetails?.id, { ProjectLiveDate: formattedValue });
        // update local projectDetails with the same PascalCase key so UI picks it up
        setProjectDetails((prev) => ({ ...prev, ProjectLiveDate: formattedValue }));
      } else {
        // regular case: field name matches backend key
        await updateProject(projectDetails?.id, { [name]: formattedValue });
        setProjectDetails((prev) => ({ ...prev, [name]: formattedValue }));
      }
    } catch (error) {
      const errMsg =
        error?.response?.data?.message || `An error occurred while updating the project. Please try again later.`;
      toast.error(errMsg);
    } finally {
      setDisabled(false);
    }
  };

  useEffect(() => setEnteredTotalHrs(projectDetails?.totalHrs), [projectDetails?.totalHrs]);

  useEffect(() => {
    const fetchClientsData = async () => {
      try {
        const response = await getAllClients();
        const data = response?.data?.data?.rows || [];
        setClientList(data);

        // First, try to find client from projectDetails.client
        const clientObj = data.find((c) => c.id === projectDetails?.clientId);
        setSelectedClient(clientObj || null);
      } catch (error) {
        const errMsg =
          error?.response?.data?.message || 'An error occurred while fetching the client list. Please try again later.';
        toast.error(errMsg);
      }
    };

    fetchClientsData();
  }, [projectDetails?.clientId, projectDetails?.client]);

  return (
    <Accordion className={classes?.accordion} expanded>
      <AccordionSummary className={classes?.summary}>
        <Typography variant="h4" color="GrayText">
          Project Details
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <Typography variant="h6">Client</Typography>
            <Autocomplete
              size="small"
              disableClearable
              disabled={!isEditPermission || disabled === 'clientId' || userIsClient}
              options={clientList}
              getOptionLabel={(option) => option?.clientName}
              value={selectedClient}
              onChange={handleClientChange}
              autoComplete
              includeInputInList
              renderInput={(params) => <TextField {...params} />}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography variant="h6">Status</Typography>
            <Select
              value={projectDetails?.status}
              onChange={handleStatusChange}
              size="small"
              fullWidth
              sx={{ textTransform: 'capitalize' }}
              disabled={!isEditPermission || disabled === 'status'}
            >
              {PROJECT_STATUS_LIST.sort().map((list) => (
                <MenuItem sx={{ textTransform: 'capitalize' }} value={list} key={list}>
                  {list}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography variant="h6">Project Estimated Hours</Typography>
            <TextField
              fullWidth
              type="text"
              size="small"
              autoComplete="off"
              inputMode="numeric"
              disabled={!isEditPermission || disabled === 'initialEstimatedHrs'}
              value={enteredInitialEstimatedHrs}
              onChange={handleInitialEstimatedHrsChange}
              onBlur={handleInitialEstimatedHrsBlur}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography variant="h6">Budgeted Hours</Typography>
            <TextField
              fullWidth
              type="text"
              size="small"
              autoComplete="off"
              inputMode="numeric"
              disabled={!isEditPermission || disabled === 'totalHrs'}
              value={enteredTotalHrs}
              onChange={hanldeTotalHrsChange}
              onBlur={handleTotalHrsBlur}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography variant="h6">Planned Start Date</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                name="startDate"
                value={projectDetails?.startDate ? dayjs(projectDetails.startDate) : null}
                onChange={(newValue) => handleDateChange('startDate')(newValue)}
                inputFormat="DD MMM YYYY"
                renderInput={(params) => <TextField size="small" {...params} helperText="" fullWidth />}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} sm={4}>
            {/* Empty space */}
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography variant="h6">Planned End Date</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                name="endDate"
                value={projectDetails?.endDate ? dayjs(projectDetails.endDate) : null}
                onChange={(newValue) => handleDateChange('endDate')(newValue)}
                inputFormat="DD MMM YYYY"
                renderInput={(params) => <TextField size="small" {...params} helperText="" fullWidth />}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography variant="h6">Actual Start Date</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                name="actualStartDate"
                value={projectDetails?.actualStartDate ? dayjs(projectDetails.actualStartDate) : null}
                onChange={(newValue) => handleDateChange('actualStartDate')(newValue)}
                inputFormat="DD MMM YYYY"
                renderInput={(params) => <TextField size="small" {...params} helperText="" fullWidth />}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} sm={4}>
            {/* Empty space */}
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography variant="h6">Actual End Date</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                name="actualEndDate"
                value={projectDetails?.actualEndDate ? dayjs(projectDetails.actualEndDate) : null}
                onChange={(newValue) => handleDateChange('actualEndDate')(newValue)}
                inputFormat="DD MMM YYYY"
                renderInput={(params) => <TextField size="small" {...params} helperText="" fullWidth />}
              />
            </LocalizationProvider>
          </Grid>

          {/* Project UAT Date */}
          <Grid item xs={12} sm={4}>
            <Typography variant="h6">Project UAT Date</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                name="projectUATDate"
                value={projectDetails?.projectUATDate ? dayjs(projectDetails.projectUATDate) : null}
                onChange={(newValue) => handleDateChange('projectUATDate')(newValue)}
                inputFormat="DD MMM YYYY"
                renderInput={(params) => <TextField size="small" {...params} helperText="" fullWidth />}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} sm={4}>
            {/* Empty space */}
          </Grid>

          {/* Project Live Date (backend key: ProjectLiveDate) */}
          <Grid item xs={12} sm={4}>
            <Typography variant="h6">Project Release Date</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                name="projectLiveDate"
                value={projectDetails?.ProjectLiveDate ? dayjs(projectDetails.ProjectLiveDate) : null}
                onChange={(newValue) => handleDateChange('projectLiveDate')(newValue)}
                inputFormat="DD MMM YYYY"
                renderInput={(params) => <TextField size="small" {...params} helperText="" fullWidth />}
              />
            </LocalizationProvider>
          </Grid>
          {/* Project Health */}
          <Grid item xs={12} sm={4}>
            <Typography variant="h6">Health Status</Typography>
            <Select
              value={projectDetails?.health}
              onChange={handleHealthChange}
              size="small"
              fullWidth
              sx={{ textTransform: 'capitalize' }}
              disabled={!isEditPermission || disabled === 'health'}
            >
              {PROJECT_HEALTH_LIST.map((list) => (
                <MenuItem sx={{ textTransform: 'capitalize' }} value={list} key={list}>
                  {list}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={12} sm={4}>
            {/* Empty space */}
          </Grid>

          {/* Project Risk */}
          <Grid item xs={12} sm={4}>
            <Typography variant="h6">Risk Status</Typography>
            <Select
              value={projectDetails?.risk}
              onChange={handleRiskChange}
              size="small"
              fullWidth
              sx={{ textTransform: 'capitalize' }}
              disabled={!isEditPermission || disabled === 'risk'}
            >
              {PROJECT_RISK_LIST.map((list) => (
                <MenuItem sx={{ textTransform: 'capitalize' }} value={list} key={list}>
                  {list}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Stack direction="row" alignItems="center">
              <Checkbox
                checked={projectDetails?.allowOverSpent}
                {...label}
                disabled={!isEditPermission || disabled === 'allowOverSpent'}
                onChange={handleCheckChange('allowOverSpent')}
              />
              <Typography className={classes.checkBoxText}>Allow Excess Hours</Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Stack direction="row" alignItems="center">
              <Checkbox
                checked={projectDetails?.allowManualTask}
                {...label}
                disabled={!isEditPermission || disabled === 'allowManualTask'}
                onChange={handleCheckChange('allowManualTask')}
              />
              <Typography className={classes.checkBoxText}>Allow Manual Task Entry</Typography>
            </Stack>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default MoreDetails;
